import {connectSettings,cloudSettings} from './settings/cloudSettings';
import {StrategyReport} from './ui/StrategyReport';
import type {StrategyReason} from '@algoquest/shared/campaign/reasoning';
import {AccountPanel} from './ui/AccountPanel';
import {preferences} from './settings/preferences';
import {useAppStore} from './stores/appStore';
import {logout,forgetSession} from './services/api/authClient';
import {suspendSync,resumeSync} from './services/api/syncClient';
import {campaignPlan,isChallenge} from '@algoquest/shared/campaign/challenge';
import { StrategyPicker } from './ui/StrategyPicker';
import { isCampaignLevelUnlocked, rateCampaignAttempt, mergeBestStars, operationBudget } from '@algoquest/shared/campaign/progression';
import { useEffect, useRef, useState } from 'react';
import { InteractiveTutorial } from './ui/InteractiveTutorial';
import type { AlgorithmId, AlgorithmOperation, CampaignPhaseResult } from '@algoquest/schemas';
import type { SubmitAttemptRequest } from '@algoquest/contracts';
import { GameCanvas, type GameCanvasHandle } from './core/GameCanvas';
import { HUD } from './ui/HUD';
import { CampaignMenu } from './ui/CampaignMenu';
import { LoadingScreen } from './ui/LoadingScreen';
import { campaignLevel, campaignWorlds, nextCampaignLevel } from './levels/campaign';
import { useSessionStore } from './stores/sessionStore';
import { useSearchAlgorithmStore } from './stores/searchAlgorithmStore';
import { useSortAlgorithmStore } from './stores/sortAlgorithmStore';
import type { SearchViewState } from './algorithms/searching/SearchController';
import type { SortViewState } from './algorithms/sorting/SortController';
import { configureCampaignStorage, enqueueCompletedAttempt, getCachedProgress, isPersistenceDegraded, listOutboxAttempts } from './services/db/localDb';
import { runFullSync } from './services/api/syncClient';

/** null user is a read-only campaign preview: no auth, writes, or synchronization. */
export function CampaignGame({userId,onSignedOut}: {userId: string | null;onSignedOut?:()=>void}) {
  useEffect(()=>{if(userId)return connectSettings(userId);},[userId]);
  const user=useAppStore(state=>state.user);
  const [accountOpen,setAccountOpen]=useState(false);
  const accountResume=useRef(false);
  const [tutorial,setTutorial]=useState<{algorithm:AlgorithmId;next:number|null;resume:boolean}|null>(null);
  const trained=useRef(new Set<string>());
  const offered=useRef(new Set<string>());
  const trainingKey=`algoquest-tutorial-v1-${userId??'preview'}`;
  useEffect(()=>{try {const saved=JSON.parse(localStorage.getItem(trainingKey)??'[]');if(Array.isArray(saved))trained.current=new Set(saved.filter(v=>typeof v==='string'));}catch{}},[trainingKey]);
  const gameRef=useRef<GameCanvasHandle>(null);
  const [number,setNumber]=useState(1);
  const [strategyLevel,setStrategyLevel]=useState<number|null>(null);
  const strategyReason=useRef<StrategyReason|null>(null);
  const [reportOperations,setReportOperations]=useState<number|null>(null);
  const plan=useRef(campaignPlan(campaignLevel(1).definition,campaignLevel(1).definition.algorithm));
  const phaseIndex=useRef(0);
  const [phase,setPhase]=useState(0);
  const phaseResults=useRef<CampaignPhaseResult[]>([]);
  const phaseTraces=useRef<AlgorithmOperation[]>([]);
  const [resultSummary,setResultSummary]=useState('');
  const activeLevel=useRef(campaignLevel(1));
  const [menu,setMenu]=useState(true);
  const [started,setStarted]=useState(false);
  const [ready,setReady]=useState(!userId);
  const [completed,setCompleted]=useState<Set<number>>(new Set());
  const [stars,setStars]=useState<Map<number,number>>(new Map());
  const sessionStars=useRef(new Map<number,number>());
  const [earnedStars,setEarnedStars]=useState(0);
  const [metrics,setMetrics]=useState({mistakes:0,hints:0});
  const [hint,setHint]=useState('');
  const [pending,setPending]=useState<Set<number>>(new Set());
  const [syncMessage,setSyncMessage]=useState(userId?'Loading campaign progress…':'Campaign preview · progress lasts for this session only.');
  const [syncFailed,setSyncFailed]=useState(false);
  const [saving,setSaving]=useState(false);
  const alive=useRef(true);
  const sessionCompleted=useRef(new Set<number>());
  const setPaused=useSessionStore(state=>state.setPaused);
  const isPaused=useSessionStore(state=>state.isPaused);
  const attempt=useRef({id:crypto.randomUUID(),startedAt:Date.now(),submitted:false,mistakes:0,hints:0});
  const pausedAt=useRef<number|null>(null);
  const definition=activeLevel.current.definition;
  const intermediate=phase<plan.current.length-1;
  const totalBudget=plan.current.reduce((sum,p)=>sum+operationBudget(p),0);
  const stage=definition.dataset.kind;
  const next=nextCampaignLevel(number);

  const refreshProgress=async()=>{
    const [rows,outbox]=await Promise.all([getCachedProgress(),listOutboxAttempts()]);
    if(!alive.current) return;
    setCompleted(new Set([...rows.filter(row=>row.completed).map(row=>row.globalLevelNumber),...outbox.map(row=>row.globalLevelNumber),...sessionCompleted.current]));
    setStars(mergeBestStars(new Map(rows.filter(r=>r.completed).map(r=>[r.globalLevelNumber,Math.max(1,r.bestStars??0)])),mergeBestStars(...outbox.map(r=>new Map([[r.globalLevelNumber,rateCampaignAttempt(r.levelDefinition,r)]]))),sessionStars.current));
    setPending(new Set(outbox.map(row=>row.globalLevelNumber)));
  };
  const sync=async()=>{
    if(!userId) return;
    setSyncMessage('Syncing campaign progress…');setSyncFailed(false);
    try {
      await runFullSync();
      if(!alive.current) return;
      setSyncMessage('Campaign progress is up to date.');
    } catch(error) {
      if(!alive.current) return;
      setSyncFailed(true);
      setSyncMessage(error instanceof Error && error.name==='CampaignDefinitionMismatch' ? error.message : 'Could not sync. Saved attempts stay on this device; you can continue playing.');
    }
    try {await refreshProgress();} catch {if(alive.current) setSyncMessage('Progress could not be read from this device.');}
  };
  useEffect(()=>{
    alive.current=true;
    if(!userId) return ()=>{alive.current=false;};
    resumeSync();
    configureCampaignStorage(userId);
    void (async()=>{
      try {await refreshProgress();}
      catch {if(alive.current) setSyncMessage('Cached progress could not be read. Trying the server…');}
      finally {if(alive.current) setReady(true);}
      if(alive.current) await sync();
    })();
    const online=()=>{void sync();};
    const offline=()=>{setSyncMessage('Offline · completed levels are saved on this device. Sync resumes when your connection returns.');};
    window.addEventListener('online',online);window.addEventListener('offline',offline);
    return ()=>{alive.current=false;window.removeEventListener('online',online);window.removeEventListener('offline',offline);};
  },[userId]);

  const resetAttempt=()=>{
    attempt.current={id:crypto.randomUUID(),startedAt:Date.now(),submitted:false,mistakes:0,hints:0};pausedAt.current=null;setMetrics({mistakes:0,hints:0});setHint('');setEarnedStars(0);
  };
  const saveCompletion=(view:SearchViewState|SortViewState,operations:AlgorithmOperation[])=>{
    if(!view.isComplete || attempt.current.submitted) return;
    attempt.current.submitted=true;
    // Captured now, before navigation can change the selected world/algorithm.
    const level=activeLevel.current.definition;
    const n=level.coordinates.globalLevelNumber;

    const sorting='algorithm' in view;
    const result:CampaignPhaseResult={algorithm:level.algorithm,found:sorting?null:view.foundIndex,comparisons:view.comparisons,swaps:sorting?view.swaps:0,shifts:sorting?view.shifts:0,passes:sorting?view.passesCompleted:0,mistakes:attempt.current.mistakes,hints:attempt.current.hints,durationMs:Math.max(0,(pausedAt.current??Date.now())-attempt.current.startedAt)};
    phaseResults.current.push(result);
    const traceOffset=phaseTraces.current.length;
    phaseTraces.current.push(...operations.map((op,i)=>({...op,sequence:traceOffset+i}))); 
    const results=phaseResults.current;
    const sum=(key:'comparisons'|'swaps'|'shifts'|'passes'|'mistakes'|'hints'|'durationMs')=>results.reduce((total,p)=>total+p[key],0);
    setResultSummary(results.map((r,i)=>`Phase ${i+1} · ${r.algorithm.replaceAll('-',' ')}: ${r.comparisons} comparisons, ${r.swaps} swaps, ${r.shifts} shifts`).join(' | ')+` · Total operations: ${sum('comparisons')+sum('swaps')+sum('shifts')}`);
    if(phaseIndex.current<plan.current.length-1)return;
    sessionCompleted.current.add(n);setCompleted(previous=>new Set([...previous,n]));
    const canonical=campaignLevel(n).definition;
    const request:SubmitAttemptRequest={attemptId:attempt.current.id,globalLevelNumber:n,algorithm:plan.current[0]!.algorithm,...(strategyReason.current?{strategyReason:strategyReason.current}:{}),found:result.found,comparisons:sum('comparisons'),swaps:sum('swaps'),shifts:sum('shifts'),passes:sum('passes'),mistakes:sum('mistakes'),hints:sum('hints'),durationMs:sum('durationMs'),...(isChallenge(canonical)?{phaseResults:[...results]}:{})};
    if(isChallenge(canonical))setReportOperations(request.comparisons+request.swaps+request.shifts);
    setMetrics({mistakes:request.mistakes,hints:request.hints});
    const rating=rateCampaignAttempt(canonical,request);setEarnedStars(rating);
    sessionStars.current.set(n,Math.max(sessionStars.current.get(n)??0,rating));
    setStars(previous=>mergeBestStars(previous,sessionStars.current));
    if(!userId) return;
    setSaving(true);
    void (async()=>{
      try {
        await enqueueCompletedAttempt(request,[...phaseTraces.current],canonical);
        if(!alive.current) return;
        setPending(previous=>new Set([...previous,n]));
        setSaving(false);
        await sync();
        if(alive.current && isPersistenceDegraded()) setSyncMessage('Local storage is unavailable. Unsynced progress lasts only while this tab stays open.');
      } catch {
        if(alive.current) {setSyncMessage('This result could not be saved on this device. Replay the level to try saving again.');setSyncFailed(false);}
      } finally {if(alive.current)setSaving(false);}
    })();
  };
  const onSearchReset=(view:SearchViewState)=>{useSearchAlgorithmStore.getState().reset(view);resetAttempt();};
  const onSortReset=(view:SortViewState)=>{useSortAlgorithmStore.getState().reset(view);resetAttempt();};
  const onSearchStep=(view:SearchViewState,ops:AlgorithmOperation[])=>{
    const store=useSearchAlgorithmStore.getState();store.setView(view);store.appendTrace(ops);
    saveCompletion(view,[...useSearchAlgorithmStore.getState().trace]);
  };
  const onSortStep=(view:SortViewState,ops:AlgorithmOperation[])=>{
    const store=useSortAlgorithmStore.getState();store.setView(view);store.appendTrace(ops);
    saveCompletion(view,[...useSortAlgorithmStore.getState().trace]);
  };
  const pause=()=>{gameRef.current?.pause();setPaused(true);if(pausedAt.current===null)pausedAt.current=Date.now();};
  const resume=()=>{if(pausedAt.current!==null)attempt.current.startedAt+=Date.now()-pausedAt.current;pausedAt.current=null;gameRef.current?.resume();setPaused(false);setMenu(false);};
  const openMenu=()=>{pause();setMenu(true);};
  const openAccount=()=>{if(saving)return;accountResume.current=started&&!menu&&!isPaused;if(started)pause();setAccountOpen(true);};
  const closeAccount=()=>{setAccountOpen(false);if(accountResume.current)resume();};
  const signOut=async()=>{await cloudSettings.pause();await suspendSync();try{await logout();onSignedOut?.();}catch(error){resumeSync();cloudSettings.resume();throw error;}};
  const launch=(n:number,algorithm?:AlgorithmId,reason?:StrategyReason)=>{
    if(saving || !isCampaignLevelUnlocked(n,completed)) return;
    const canonical=campaignLevel(n).definition;
    strategyReason.current=reason??null;setReportOperations(null);
    plan.current=campaignPlan(canonical,algorithm??canonical.algorithm);
    phaseIndex.current=0;setPhase(0);phaseResults.current=[];phaseTraces.current=[];setResultSummary('');
    const level={definition:plan.current[0]!};
    activeLevel.current=level;
    setNumber(n);setStarted(true);setMenu(false);setPaused(false);
    gameRef.current?.loadLevel(level);gameRef.current?.resume();
  };
  const play=(n:number)=>{
    if(saving || !isCampaignLevelUnlocked(n,completed)) return;
    if(isChallenge(campaignLevel(n).definition)){if(started)pause();setStrategyLevel(n);return;}
    const algorithm=campaignLevel(n).definition.algorithm;
    if(preferences.get().autoTutorial&&!trained.current.has(algorithm)&&!offered.current.has(algorithm)){
      offered.current.add(algorithm);if(started)pause();setTutorial({algorithm,next:n,resume:false});return;
    }
    launch(n);
  };
  const nextPhase=()=>{
    const index=phaseIndex.current+1;
    const previous=useSortAlgorithmStore.getState().view;
    if(!previous?.isComplete||!plan.current[index])return;
    const nextDefinition=plan.current[index]!;
    // Use the actual sorted output, never a newly generated search dataset.
    activeLevel.current={definition:{...nextDefinition,dataset:{...nextDefinition.dataset,values:[...previous.values]}}};
    phaseIndex.current=index;setPhase(index);setPaused(false);
    gameRef.current?.loadLevel(activeLevel.current);gameRef.current?.resume();
  };
  const restart=()=>launch(number,plan.current[0]!.algorithm,strategyReason.current??undefined);
  const closeTutorial=(done:boolean)=>{
    if(!tutorial)return;
    if(done){trained.current.add(tutorial.algorithm);try{localStorage.setItem(trainingKey,JSON.stringify([...trained.current]));}catch{}}
    const current=tutorial;setTutorial(null);
    if(current.next!==null)launch(current.next);else if(current.resume)resume();
  };
  const openTutorial=(algorithm:AlgorithmId,fromGame=false)=>{
    const shouldResume=fromGame&&!isPaused;if(started)pause();setTutorial({algorithm,next:null,resume:shouldResume});
  };
  useEffect(()=>{
    const onHidden=()=>{if(document.hidden&&started&&!menu&&!isPaused)pause();};
    document.addEventListener('visibilitychange',onHidden);
    return ()=>document.removeEventListener('visibilitychange',onHidden);
  },[started,menu,isPaused]);
  const reject=()=>{if(!attempt.current.submitted){attempt.current.mistakes++;setMetrics({mistakes:attempt.current.mistakes,hints:attempt.current.hints});}};
  const showHint=()=>{
    if(attempt.current.submitted || isPaused) return;
    attempt.current.hints++;setMetrics({mistakes:attempt.current.mistakes,hints:attempt.current.hints});
    setHint(definition.algorithm==='linear-search'?'Scan packages from left to right, one at a time.':definition.algorithm==='binary-search'?'Inspect the middle package. Keep the left half if the target is smaller; otherwise keep the right half.':definition.algorithm==='bubble-sort'?`Compare neighbours. Swap if they are out of ${definition.dataset.kind==='sorting'?definition.dataset.sortDirection:'ascending'} order.`:definition.algorithm==='selection-sort'?'Compare the scan value with your candidate. Select the better candidate for the required order.':'Compare the carried key with the sorted value. Shift values that belong after the key; otherwise insert.');
  };
  if(!ready) return <LoadingScreen message="Loading your campaign…"/>;
  return <div className={`campaign-game relative h-screen w-screen overflow-hidden bg-slate-900${!userId?' preview-root':''}`}>
    {!userId && <div className="campaign-preview-banner">Campaign preview · Progress is not saved to an account.</div>}
    {started && <>
      <GameCanvas ref={gameRef} initialLevel={activeLevel.current} onNearestBoxChange={index=>useSessionStore.getState().setNearestBoxIndex(index)}
        onViewReset={onSearchReset} onViewStep={onSearchStep} onActionRejected={index=>{reject();useSearchAlgorithmStore.getState().setRejectedHint(index);}} onChoiceRejected={()=>{reject();useSearchAlgorithmStore.getState().setChoiceRejected(true);window.setTimeout(()=>useSearchAlgorithmStore.getState().setChoiceRejected(false),100);}}
        onSortViewReset={onSortReset} onSortViewStep={onSortStep} onSortActionRejected={index=>{reject();useSortAlgorithmStore.getState().setRejectedHint(index);}} onSortDecisionRejected={()=>{reject();useSortAlgorithmStore.getState().setDecisionRejected(true);window.setTimeout(()=>useSortAlgorithmStore.getState().setDecisionRejected(false),100);}}/>
      {!menu && <HUD key={`${number}-${phase}`} stage={stage} mode={definition.algorithm==='binary-search'?'binary-search':'linear-search'} sortAlgorithm={definition.algorithm==='selection-sort'?'selection-sort':definition.algorithm==='insertion-sort'?'insertion-sort':'bubble-sort'}
        levelLabel={`World ${definition.coordinates.world} · Level ${number} / 100${plan.current.length>1?` · Phase ${phase+1}/2`:""}`} target={definition.dataset.kind==='searching'?definition.dataset.target:0} sortDirection={definition.dataset.kind==='sorting'?definition.dataset.sortDirection:'ascending'} levelIndex={number-1} levelCount={100}
        onTogglePause={()=>isPaused?resume():pause()} onRestart={restart} onResetCamera={()=>gameRef.current?.resetCamera()}
        onPrevLevel={()=>number>1 && play(number-1)} onNextLevel={()=>intermediate?nextPhase():next===null?openMenu():play(next)}
        onSwitchStage={()=>{}} onSwitchMode={()=>{}} onSwitchSortAlgorithm={()=>{}} showLevelSwitcher={false}
        onAccount={userId&&user?openAccount:undefined}
        onOpenCampaign={openMenu} nextLevelLabel={intermediate?'Continue to Binary Search →':next===null?'Back to campaign':number%10===0?`World ${definition.coordinates.world+1} →`:'Next level →'} nextLevelDisabled={saving}
        onTutorial={()=>openTutorial(definition.algorithm,true)} syncStatus={intermediate?'Complete both phases to save this Lab level.':syncMessage}
        earnedStars={earnedStars} mistakes={metrics.mistakes} hints={metrics.hints} hint={hint} onHint={showHint} operationBudget={totalBudget} intermediate={intermediate} resultSummary={resultSummary} resultDetails={reportOperations!==null?<StrategyReport level={campaignLevel(number).definition} algorithm={plan.current[0]!.algorithm} reason={strategyReason.current} operations={reportOperations}/>:undefined}
        learningObjective={definition.learningObjectives[0]} worldName={campaignWorlds[definition.coordinates.world-1]!.name}/>}
    </>}
    {menu && <CampaignMenu key={`${number}-${menu}`} onAccount={userId&&user?openAccount:undefined} selectedLevel={number} completed={completed} stars={stars} pending={pending} onPlay={play} onTutorial={algorithm=>openTutorial(algorithm)} onResume={started?resume:undefined} syncMessage={syncMessage} onRetry={userId && syncFailed?()=>void sync():undefined}/>}
    {strategyLevel!==null&&<StrategyPicker level={campaignLevel(strategyLevel).definition} onCancel={()=>{setStrategyLevel(null);setMenu(true);}} onChoose={(algorithm,reason)=>{const n=strategyLevel;setStrategyLevel(null);launch(n,algorithm,reason);}}/>}
    {accountOpen&&user&&<AccountPanel user={user} pending={pending.size} syncStatus={syncMessage} onClose={closeAccount} onLogout={signOut} onCredentialsChanged={async()=>{forgetSession();onSignedOut?.();}} onUpdated={updated=>useAppStore.getState().setUser(updated)}/>}
    {tutorial&&<InteractiveTutorial key={tutorial.algorithm} algorithm={tutorial.algorithm} onClose={closeTutorial}/>}
  </div>;
}
